import { ratingLanes } from "@nlc/shared-types";
import { useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogTitle,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { useRatingSummary } from "@/features/request/edit/hooks/useRatingSummary";
import { useToggleCompletion } from "@/features/request/edit/hooks/useToggleCompletion";
import { useUser } from "@/hooks/useUser";

import { RatingLane } from "./RatingLane";
import { SummaryCards } from "./SummaryCards";
import { emptyLanes, useRatingLanes } from "./useRatingLanes";

interface RatingModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted?: () => void;
}

export function RatingModal({
  isOpen,
  onOpenChange,
  onSubmitted,
}: RatingModalProps) {
  const { data: user } = useUser();
  const { completeRequest, isPending } = useToggleCompletion();
  const { data: summary } = useRatingSummary(isOpen);
  const { hasRole } = useRoleAccess();

  const { lanes, setLanes, setStars, toggleChip } = useRatingLanes();
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [dismissInfoOpen, setDismissInfoOpen] = useState(false);

  const firstName = user?.user.name?.trim().split(/\s+/)[0] ?? "there";
  const canSubmit = ratingLanes.every((lane) => lanes[lane].stars > 0);
  const canSkip = hasRole(["super-admin", "team-leader", "customer-success"]);

  const reset = () => {
    setLanes(emptyLanes);
    setComment("");
    setSubmitted(false);
  };

  const close = (open: boolean) => {
    if (!open) {
      reset();
    }
    onOpenChange(open);
  };

  const handleSkip = () => {
    completeRequest({ action: "complete" }, () => {
      close(false);
      onSubmitted?.();
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !submitted && !isPending) {
      setDismissInfoOpen(true);
      onOpenChange(false);

      return;
    }
    close(open);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    completeRequest(
      {
        action: "complete",
        rating: {
          comment: comment || undefined,
          design: lanes.design.stars,
          designChips: lanes.design.chips,
          platform: lanes.platform.stars,
          platformChips: lanes.platform.chips,
          team: lanes.team.stars,
          teamChips: lanes.team.chips,
        },
      },
      () => {
        setSubmitted(true);
        setTimeout(() => {
          close(false);
          onSubmitted?.();
        }, 1200);
      },
    );
  };

  return (
    <>
      <Modal
        classNames={{ modal: "w-[640px]" }}
        isDismissable
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Dialog>
          <DialogBody>
            <div className="flex flex-col gap-lg">
              <div>
                <DialogTitle className="text-xl font-semibold text-text-primary">
                  {firstName}, how did your project turn out?
                </DialogTitle>
                <DialogCloseButton />
                <p className="mt-xs text-sm text-text-tertiary">
                  A minute of your time helps us make every future design better
                  for you.
                </p>
              </div>

              <SummaryCards summary={summary} />

              {ratingLanes.map((lane) => (
                <RatingLane
                  chips={lanes[lane].chips}
                  isDisabled={submitted}
                  key={lane}
                  lane={lane}
                  onStarsChange={(stars) => setStars(lane, stars)}
                  onToggleChip={(chip) => toggleChip(lane, chip)}
                  stars={lanes[lane].stars}
                />
              ))}

              <div className="flex flex-col gap-xs">
                <p className="text-md font-semibold text-text-primary">
                  Anything else on your mind?
                </p>
                <p className="text-sm text-text-tertiary">
                  Even one sentence can help us improve your deliverables.
                </p>
                <div className="relative">
                  <textarea
                    className="w-full resize-none rounded-lg border border-border-primary bg-bg-primary p-md pb-xl text-sm text-text-primary outline-hidden focus-visible:border-border-brand-solid disabled:bg-bg-secondary"
                    disabled={submitted}
                    maxLength={1000}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="e.g. The revisions were fast, but I wish I could see the revision history."
                    rows={3}
                    value={comment}
                  />
                  <span className="pointer-events-none absolute bottom-md right-md text-xs text-text-tertiary">
                    {comment.length}/1000
                  </span>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              className="w-full"
              isDisabled={!canSubmit || isPending || submitted}
              onPress={handleSubmit}
              size="lg"
              variant="primary"
            >
              {submitted ? (
                <span className="animate-in fade-in duration-200">
                  <span className="inline-block animate-grow">✓</span> Feedback
                  submitted
                </span>
              ) : isPending ? (
                "Submitting..."
              ) : (
                "Send my feedback"
              )}
            </Button>

            {canSkip && (
              <Button
                color="gray"
                isDisabled={isPending || submitted}
                onPress={handleSkip}
                size="lg"
                variant="tertiary"
              >
                Skip
              </Button>
            )}
          </DialogFooter>
        </Dialog>
      </Modal>

      <Modal
        classNames={{ modal: "w-[400px]" }}
        isDismissable
        isOpen={dismissInfoOpen}
        onOpenChange={setDismissInfoOpen}
      >
        <Dialog>
          <DialogBody>
            <div className="flex flex-col gap-xs">
              <DialogTitle className="text-lg font-semibold text-text-primary">
                This request isn&apos;t complete yet
              </DialogTitle>
              <DialogCloseButton />
              <p className="text-sm text-text-tertiary">
                Your rating is the final step. Until you rate the design, this
                request stays open and won&apos;t be marked complete.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              className="w-full"
              onPress={() => {
                setDismissInfoOpen(false);
                onOpenChange(true);
              }}
              size="lg"
              variant="primary"
            >
              Keep rating
            </Button>
            <Button
              color="gray"
              onPress={() => {
                setDismissInfoOpen(false);
                reset();
              }}
              size="lg"
              variant="tertiary"
            >
              Got it
            </Button>
          </DialogFooter>
        </Dialog>
      </Modal>
    </>
  );
}
